import {
  OfferTypeDto,
  ProcessOfferDto,
  ProcessOfferGroupItemDto,
  ProcessOfferSubgroupItemDto,
} from '@/api/generated';
import {
  insertedSetsPricing,
  subgroupCostPerPerson,
} from '@/views/offerGroupDefinitions/util/definitionPricing.util.ts';

export const isPackageOffer = (offer: ProcessOfferDto): boolean =>
  offer.offerType === OfferTypeDto.Package;

export const isGroupIncluded = (group: ProcessOfferGroupItemDto): boolean =>
  !group.optional || group.selected;

export const selectedOptionalSurchargePerPerson = (offer: ProcessOfferDto): number =>
  (offer.groups ?? [])
    .filter(group => group.optional && group.selected)
    .reduce((acc, group) => acc + (group.surchargePerPerson ?? 0), 0);

export type PackageSelectionValidation = {
  warnings: PackageSelectionWarning[];
};

export type PackageSelectionWarning =
  | {
      computed: number;
      declared: number;
      definitionName: string;
      kind: 'setUnderpriced';
      processOfferGroupId: number;
      sourceDefinitionId: number;
    }
  | {
      groupName: string;
      guests: number;
      kind: 'minGuests';
      minGuests: number;
      processOfferGroupId: number;
    };

export const validatePackageSelection = (offer: ProcessOfferDto): PackageSelectionValidation => {
  const warnings: PackageSelectionWarning[] = [];
  const offerGuests = offer.details.noOfGuests ?? 0;
  (offer.groups ?? []).filter(isGroupIncluded).forEach(group => {
    const guests = group.noOfGuests ?? offerGuests;
    if (group.minGuests == null || guests <= 0 || guests >= group.minGuests) {
      return;
    }
    warnings.push({
      groupName: group.name,
      guests,
      kind: 'minGuests',
      minGuests: group.minGuests,
      processOfferGroupId: group.processOfferGroupId,
    });
  });
  insertedSetsPricing(offer)
    .filter(set => set.underpriced)
    .forEach(set =>
      warnings.push({
        computed: set.computedPerPerson,
        declared: set.declaredPerPerson ?? 0,
        definitionName: set.definitionName,
        kind: 'setUnderpriced',
        processOfferGroupId: set.processOfferGroupIds[0],
        sourceDefinitionId: set.sourceDefinitionId,
      }),
    );

  return { warnings };
};

export const selectedDishesTotal = (
  dishes: { selected?: boolean; totalPrice?: number }[] | undefined,
): number =>
  (dishes ?? [])
    .filter(dish => dish.selected)
    .reduce((acc, dish) => acc + (dish.totalPrice ?? 0), 0);

export const selectedPortions = (
  dishes: { quantity?: number; selected?: boolean }[] | undefined,
): number =>
  (dishes ?? []).filter(dish => dish.selected).reduce((acc, dish) => acc + (dish.quantity ?? 0), 0);

export const isUnlimitedSubgroup = (subgroup: { unlimited?: boolean }): boolean =>
  subgroup.unlimited === true;

export const groupSelectionCostPerPerson = (group: ProcessOfferGroupItemDto): number => {
  const guests = group.noOfGuests ?? 0;
  if (guests <= 0) {
    return 0;
  }
  const cost = (group.subgroups ?? [])
    .filter(subgroup => !isUnlimitedSubgroup(subgroup))
    .reduce((acc, subgroup) => acc + selectedDishesTotal(subgroup.dishes), 0);
  return cost / guests;
};

export const isSetSubgroup = (
  subgroup: Pick<ProcessOfferSubgroupItemDto, 'maxDistinctDishes'>,
): boolean => subgroup.maxDistinctDishes != null;

export const isSetGroup = (group: Pick<ProcessOfferGroupItemDto, 'sourceDefinitionId'>): boolean =>
  group.sourceDefinitionId != null;

export const isLockedSetSubgroup = ({
  group,
  isPredefinedTemplate,
  subgroup,
}: {
  group?: Pick<ProcessOfferGroupItemDto, 'sourceDefinitionId'>;
  isPredefinedTemplate: boolean;
  subgroup: Pick<ProcessOfferSubgroupItemDto, 'maxDistinctDishes'>;
}): boolean =>
  !isPredefinedTemplate && (isSetSubgroup(subgroup) || (group != null && isSetGroup(group)));

export const subgroupDisplayTotal = (subgroup: ProcessOfferSubgroupItemDto): number =>
  isUnlimitedSubgroup(subgroup)
    ? 0
    : subgroup.maxDistinctDishes == null
      ? subgroup.price
      : selectedDishesTotal(subgroup.dishes);

export const groupDisplayTotal = (group: ProcessOfferGroupItemDto): number =>
  isSetGroup(group)
    ? group.price
    : (group.subgroups ?? []).reduce((acc, sub) => acc + subgroupDisplayTotal(sub), 0) +
      (group.elements ?? []).reduce((acc, element) => acc + element.price, 0);

export const computePackageTotal = (offer: ProcessOfferDto): number =>
  ((offer.packagePricePerPerson ?? 0) + selectedOptionalSurchargePerPerson(offer)) *
  Math.max(offer.details.noOfGuests ?? 0, 1);

export const computeReferenceCost = (offer: ProcessOfferDto): number => {
  const perPerson = (offer.groups ?? [])
    .filter(isGroupIncluded)
    .reduce(
      (acc, group) =>
        acc +
        (group.subgroups ?? []).reduce((subAcc, sub) => subAcc + subgroupCostPerPerson(sub), 0),
      0,
    );
  return perPerson * Math.max(offer.details.noOfGuests ?? 0, 1);
};
